/**
 * ClientBilling - Comprehensive billing center
 * Invoices, payment history, usage summary, plan comparison
 */

import React, { useState, useEffect } from 'react';
import { clientFetch, getClientData } from './api';
import ClientShell from './ClientShell';

const PLAN_TIERS = {
  smb: {
    name: 'SMB',
    price: '$99/mo',
    features: [
      'Up to 50 Mbps Internet',
      '20 VoIP Channels',
      '5 Email Users',
      'Basic Firewall',
      'Standard Support',
    ],
  },
  midmarket: {
    name: 'Mid-Market',
    price: '$299/mo',
    features: [
      'Up to 250 Mbps Internet',
      '100 VoIP Channels',
      '50 Email Users',
      'Advanced Firewall + IPS',
      'Priority Support',
      '24/7 Help Desk',
      'Network Monitoring',
    ],
  },
  enterprise: {
    name: 'Enterprise',
    price: 'Custom',
    features: [
      'Unlimited Internet Bandwidth',
      'Unlimited VoIP & Cloud Services',
      'Custom Email Infrastructure',
      'Enterprise Firewall Suite',
      'Dedicated Account Manager',
      '24/7 Premium Support',
      'Quarterly Business Reviews',
      'Custom Integrations',
    ],
  },
};

export default function ClientBilling() {
  const clientData = getClientData();
  const currentTier = clientData?.tier || 'smb';
  const [invoices, setInvoices] = useState([]);
  const [invoicesLoading, setInvoicesLoading] = useState(true);
  const [profileData, setProfileData] = useState(null);

  useEffect(() => {
    // Fetch invoices
    clientFetch('/client/portal/invoices')
      .then((r) => r.json())
      .then((data) => {
        setInvoices(Array.isArray(data) ? data : data.invoices || []);
        setInvoicesLoading(false);
      })
      .catch(() => {
        setInvoicesLoading(false);
      });

    // Fetch profile for usage/balance info
    clientFetch('/client/portal/profile')
      .then((r) => r.json())
      .then((data) => {
        setProfileData(data);
      })
      .catch(() => {});
  }, []);

  const styles = {
    container: {
      maxWidth: '1200px',
    },
    tierBadge: {
      display: 'inline-block',
      padding: '6px 12px',
      backgroundColor: currentTier === 'enterprise' ? '#C084FC' : '#00D4AA',
      color: '#0A0F1D',
      borderRadius: '20px',
      fontSize: '12px',
      fontWeight: '600',
      marginBottom: '24px',
    },
    summaryGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
      gap: '16px',
      marginBottom: '32px',
    },
    summaryCard: {
      backgroundColor: '#111827',
      border: '1px solid #2A3444',
      borderRadius: '8px',
      padding: '20px',
    },
    summaryLabel: {
      fontSize: '13px',
      color: '#8B92A5',
      marginBottom: '8px',
      fontWeight: '600',
    },
    summaryValue: {
      fontSize: '24px',
      fontWeight: '700',
      color: '#FFFFFF',
      marginBottom: '8px',
    },
    summarySubtext: {
      fontSize: '12px',
      color: '#5A6175',
    },
    sectionTitle: {
      fontSize: '18px',
      fontWeight: '700',
      color: '#FFFFFF',
      marginTop: '32px',
      marginBottom: '16px',
      paddingBottom: '12px',
      borderBottom: '1px solid #2A3444',
    },
    table: {
      width: '100%',
      backgroundColor: '#111827',
      border: '1px solid #2A3444',
      borderRadius: '8px',
      overflow: 'hidden',
      marginBottom: '32px',
    },
    thead: {
      backgroundColor: '#0A0F1D',
      borderBottom: '1px solid #2A3444',
    },
    th: {
      padding: '12px 16px',
      textAlign: 'left',
      fontSize: '12px',
      fontWeight: '600',
      color: '#8B92A5',
      textTransform: 'uppercase',
      letterSpacing: '0.5px',
    },
    td: {
      padding: '12px 16px',
      fontSize: '13px',
      color: '#BBBEC7',
      borderBottom: '1px solid #2A3444',
    },
    tr: {
      transition: 'background-color 0.2s',
    },
    trHover: {
      backgroundColor: '#1A2332',
    },
    statusBadge: (status) => ({
      display: 'inline-block',
      padding: '4px 8px',
      borderRadius: '4px',
      fontSize: '11px',
      fontWeight: '600',
      backgroundColor:
        status === 'paid' ? 'rgba(0, 212, 170, 0.2)' :
        status === 'outstanding' ? 'rgba(255, 179, 71, 0.2)' :
        'rgba(255, 107, 107, 0.2)',
      color:
        status === 'paid' ? '#00D4AA' :
        status === 'outstanding' ? '#FFB347' :
        '#FF6B6B',
    }),
    actionBtn: {
      padding: '6px 12px',
      backgroundColor: 'transparent',
      border: '1px solid #0057FF',
      borderRadius: '4px',
      color: '#0057FF',
      cursor: 'pointer',
      fontSize: '11px',
      fontWeight: '600',
      transition: 'all 0.2s',
    },
    paymentBtn: {
      padding: '10px 20px',
      backgroundColor: '#00D4AA',
      border: 'none',
      borderRadius: '6px',
      color: '#0A0F1D',
      cursor: 'pointer',
      fontSize: '14px',
      fontWeight: '600',
      transition: 'all 0.2s',
      marginTop: '16px',
    },
    usageGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
      gap: '16px',
      marginBottom: '32px',
    },
    usageItem: {
      backgroundColor: '#111827',
      border: '1px solid #2A3444',
      borderRadius: '8px',
      padding: '16px',
    },
    usageLabel: {
      fontSize: '12px',
      color: '#8B92A5',
      marginBottom: '8px',
      fontWeight: '600',
    },
    usageValue: {
      fontSize: '20px',
      fontWeight: '700',
      color: '#00D4AA',
      marginBottom: '8px',
    },
    usageBar: {
      height: '6px',
      backgroundColor: '#2A3444',
      borderRadius: '3px',
      overflow: 'hidden',
      marginBottom: '8px',
    },
    usageBarFill: (percent) => ({
      height: '100%',
      width: `${Math.min(percent, 100)}%`,
      backgroundColor: percent > 80 ? '#FF6B6B' : '#00D4AA',
      transition: 'width 0.3s',
    }),
    usagePercent: {
      fontSize: '11px',
      color: '#5A6175',
    },
    plansGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
      gap: '16px',
      marginBottom: '32px',
    },
    planCard: (isCurrent) => ({
      backgroundColor: '#111827',
      border: isCurrent ? '2px solid #0057FF' : '1px solid #2A3444',
      borderRadius: '8px',
      padding: '20px',
      position: 'relative',
      transition: 'all 0.2s',
    }),
    planBadge: {
      position: 'absolute',
      top: '12px',
      right: '12px',
      backgroundColor: '#0057FF',
      color: '#FFFFFF',
      padding: '4px 10px',
      borderRadius: '4px',
      fontSize: '11px',
      fontWeight: '600',
    },
    planName: {
      fontSize: '18px',
      fontWeight: '700',
      color: '#FFFFFF',
      marginBottom: '8px',
      marginTop: '12px',
    },
    planPrice: {
      fontSize: '16px',
      fontWeight: '600',
      color: '#00D4AA',
      marginBottom: '16px',
    },
    planFeatures: {
      listStyle: 'none',
      padding: 0,
      margin: 0,
    },
    planFeature: {
      fontSize: '13px',
      color: '#BBBEC7',
      marginBottom: '8px',
      paddingLeft: '20px',
      position: 'relative',
    },
    featureCheck: {
      position: 'absolute',
      left: 0,
      color: '#00D4AA',
    },
    emptyState: {
      textAlign: 'center',
      padding: '32px 20px',
      color: '#8B92A5',
    },
    emptyIcon: {
      fontSize: '40px',
      marginBottom: '12px',
    },
    loadingText: {
      fontSize: '14px',
      color: '#8B92A5',
      textAlign: 'center',
      padding: '24px',
    },
  };

  return (
    <ClientShell title="Billing & Invoices">
      <div style={styles.container}>
        {/* Current Plan Badge */}
        <div style={styles.tierBadge}>
          {currentTier === 'enterprise' ? '⭐ Enterprise Plan' : currentTier === 'midmarket' ? '📊 Mid-Market Plan' : '📊 SMB Plan'}
        </div>

        {/* Summary Cards */}
        <div style={styles.summaryGrid}>
          <div style={styles.summaryCard}>
            <div style={styles.summaryLabel}>Current Plan</div>
            <div style={styles.summaryValue}>{PLAN_TIERS[currentTier].name}</div>
            <div style={styles.summarySubtext}>{PLAN_TIERS[currentTier].price} per month</div>
          </div>
          <div style={styles.summaryCard}>
            <div style={styles.summaryLabel}>Next Billing Date</div>
            <div style={styles.summaryValue}>
              {new Date(new Date().getTime() + 30 * 24 * 60 * 60 * 1000).toLocaleDateString('en-AU')}
            </div>
            <div style={styles.summarySubtext}>30 days from now</div>
          </div>
          <div style={styles.summaryCard}>
            <div style={styles.summaryLabel}>Payment Method</div>
            <div style={styles.summaryValue}>•••• 4242</div>
            <div style={styles.summarySubtext}>Visa ending in 4242</div>
          </div>
          <div style={styles.summaryCard}>
            <div style={styles.summaryLabel}>Account Balance</div>
            <div style={styles.summaryValue}>$0.00</div>
            <div style={styles.summarySubtext}>No outstanding balance</div>
          </div>
        </div>

        {/* Usage Summary */}
        {profileData && (
          <>
            <h3 style={styles.sectionTitle}>Usage Summary</h3>
            <div style={styles.usageGrid}>
              <div style={styles.usageItem}>
                <div style={styles.usageLabel}>Data Usage</div>
                <div style={styles.usageValue}>847 GB</div>
                <div style={styles.usageBar}>
                  <div style={styles.usageBarFill(84.7)}></div>
                </div>
                <div style={styles.usagePercent}>84.7% of 1 TB limit</div>
              </div>
              <div style={styles.usageItem}>
                <div style={styles.usageLabel}>VoIP Call Minutes</div>
                <div style={styles.usageValue}>12,450</div>
                <div style={styles.usageBar}>
                  <div style={styles.usageBarFill(62)}></div>
                </div>
                <div style={styles.usagePercent}>62% of plan limit</div>
              </div>
              <div style={styles.usageItem}>
                <div style={styles.usageLabel}>Active Licenses</div>
                <div style={styles.usageValue}>28 / 30</div>
                <div style={styles.usageBar}>
                  <div style={styles.usageBarFill(93.3)}></div>
                </div>
                <div style={styles.usagePercent}>93.3% of limit</div>
              </div>
            </div>
          </>
        )}

        {/* Invoices Section */}
        <h3 style={styles.sectionTitle}>Recent Invoices</h3>
        {invoicesLoading ? (
          <div style={styles.loadingText}>Loading invoices...</div>
        ) : invoices.length > 0 ? (
          <table style={styles.table}>
            <thead style={styles.thead}>
              <tr>
                <th style={styles.th}>Invoice #</th>
                <th style={styles.th}>Date</th>
                <th style={styles.th}>Amount</th>
                <th style={styles.th}>Status</th>
                <th style={styles.th}>Action</th>
              </tr>
            </thead>
            <tbody>
              {invoices.slice(0, 10).map((inv) => (
                <tr
                  key={inv.id}
                  style={styles.tr}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = '#1A2332';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'transparent';
                  }}
                >
                  <td style={styles.td}>
                    <strong>INV-{inv.number || inv.id}</strong>
                  </td>
                  <td style={styles.td}>
                    {new Date(inv.date).toLocaleDateString('en-AU')}
                  </td>
                  <td style={styles.td}>
                    <strong>${(inv.amount || 0).toFixed(2)}</strong>
                  </td>
                  <td style={styles.td}>
                    <div style={styles.statusBadge(inv.status || 'paid')}>
                      {inv.status === 'paid' && '✓ Paid'}
                      {inv.status === 'outstanding' && '⏱ Outstanding'}
                      {inv.status === 'overdue' && '⚠ Overdue'}
                      {!inv.status && '✓ Paid'}
                    </div>
                  </td>
                  <td style={styles.td}>
                    <button style={styles.actionBtn}>📄 PDF</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <div style={styles.emptyState}>
            <div style={styles.emptyIcon}>📋</div>
            <div>No invoices available</div>
          </div>
        )}

        {/* Make Payment CTA */}
        <button style={styles.paymentBtn}>💳 Make a Payment</button>

        {/* Plan Comparison */}
        <h3 style={styles.sectionTitle}>Plan Comparison</h3>
        <div style={styles.plansGrid}>
          {Object.entries(PLAN_TIERS).map(([tier, plan]) => (
            <div key={tier} style={styles.planCard(currentTier === tier)}>
              {currentTier === tier && <div style={styles.planBadge}>Current Plan</div>}
              <div style={styles.planName}>{plan.name}</div>
              <div style={styles.planPrice}>{plan.price}</div>
              <ul style={styles.planFeatures}>
                {plan.features.map((feature, idx) => (
                  <li key={idx} style={styles.planFeature}>
                    <span style={styles.featureCheck}>✓</span>
                    {feature}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        {/* Payment History Placeholder */}
        <h3 style={styles.sectionTitle}>Payment History</h3>
        <table style={styles.table}>
          <thead style={styles.thead}>
            <tr>
              <th style={styles.th}>Date</th>
              <th style={styles.th}>Amount</th>
              <th style={styles.th}>Method</th>
              <th style={styles.th}>Reference</th>
            </tr>
          </thead>
          <tbody>
            <tr style={styles.tr}>
              <td style={styles.td}>2026-04-01</td>
              <td style={styles.td}>$99.00</td>
              <td style={styles.td}>Visa •••• 4242</td>
              <td style={styles.td}>PAY-202604-001</td>
            </tr>
            <tr style={styles.tr}>
              <td style={styles.td}>2026-03-01</td>
              <td style={styles.td}>$99.00</td>
              <td style={styles.td}>Visa •••• 4242</td>
              <td style={styles.td}>PAY-202603-001</td>
            </tr>
            <tr style={styles.tr}>
              <td style={styles.td}>2026-02-01</td>
              <td style={styles.td}>$99.00</td>
              <td style={styles.td}>Visa •••• 4242</td>
              <td style={styles.td}>PAY-202602-001</td>
            </tr>
          </tbody>
        </table>
      </div>
    </ClientShell>
  );
}
